import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { cgKeys } from './hooks';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Who a CG job is for.
 *
 * public.companies and public.leads are gated on is_sales_member(), and CG
 * membership is a different list — so a dropdown built straight off those
 * tables shows a CG person an empty box and no reason why. These two RPCs are
 * the letterbox (20260930000100): id and name, gated on is_cg_member(), with
 * deal_value, stage, notes, email and phone deliberately not in the return
 * type. Choosing who a job is for should not hand over the sales pipeline.
 */

export interface CgClientCompany {
  id: string;
  name: string;
}

export interface CgClientLead {
  id: string;
  full_name: string;
  title: string;
  company_id: string;
}

function fail(message: string): never {
  throw new Error(message);
}

export async function fetchCgClientCompanies(client: SupabaseClient): Promise<CgClientCompany[]> {
  const { data, error } = await client.rpc('cg_client_companies');
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export async function fetchCgClientLeads(
  client: SupabaseClient,
  companyId: string,
): Promise<CgClientLead[]> {
  // The argument is omitted rather than passed as undefined: p_company has a
  // default of null in SQL, and exactOptionalPropertyTypes will not let an
  // explicit undefined stand in for "not supplied".
  const { data, error } = await (companyId === ''
    ? client.rpc('cg_client_leads')
    : client.rpc('cg_client_leads', { p_company: companyId }));
  if (error) {
    fail(error.message);
  }
  return Array.isArray(data) ? data : [];
}

export const clientDirectoryKeys = {
  companies: () => [...cgKeys.all, 'client-companies'] as const,
  leads: (companyId: string) => [...cgKeys.all, 'client-leads', companyId] as const,
};

export function useCgClientCompanies(enabled = true) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: clientDirectoryKeys.companies(),
    queryFn: () => fetchCgClientCompanies(client),
    enabled: enabled && access.status === 'approved',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * Contacts at one company.
 *
 * Disabled until a company is chosen, on purpose: the RPC accepts a null
 * company and would happily return every lead in the CRM, and a dropdown of
 * every contact TAP has ever met is not a choice, it is a search problem.
 */
export function useCgClientLeads(companyId: string) {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: clientDirectoryKeys.leads(companyId),
    queryFn: () => fetchCgClientLeads(client, companyId),
    enabled: companyId !== '' && access.status === 'approved',
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
